import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiShoppingBag, FiChevronRight, FiStar, FiPackage } from 'react-icons/fi';
import { ecommerceService } from '../services';
import type { Store } from '../types';
import { Card, LoaderComponent } from './ui';

interface StoreItemProps {
  store: Store;
  onClick: () => void;
  isFeatured?: boolean;
}

const StoreItem: React.FC<StoreItemProps> = ({ store, onClick, isFeatured }) => {
  const logoSrc = store.LOGO && store.MIME_TYPE
    ? `data:${store.MIME_TYPE};base64,${store.LOGO}`
    : null;

  return (
    <div
      onClick={onClick}
      className={`flex flex-col items-center cursor-pointer group min-w-[120px] max-w-[120px] ${
        isFeatured ? 'relative' : ''
      }`}
    >
      {/* Featured Badge */}
      {isFeatured && (
        <div className="absolute -top-1 -right-1 z-10">
          <span className="flex items-center gap-0.5 bg-pagali-primary text-white text-[10px] font-semibold px-2 py-0.5 rounded-full shadow-sm">
            <FiStar className="text-[8px]" />
            Destaque
          </span>
        </div>
      )}

      {/* Logo Container */}
      <div
        className={`w-20 h-20 rounded-2xl bg-white shadow-card flex items-center justify-center overflow-hidden transition-all duration-200 group-hover:shadow-card-hover group-hover:scale-[1.02] ${
          isFeatured
            ? 'ring-2 ring-pagali-primary/30'
            : 'border border-pagali-gray-100'
        }`}
      >
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={store.NOME}
            className="w-16 h-16 object-contain"
          />
        ) : (
          <FiShoppingBag className="text-2xl text-pagali-gray-400" />
        )}
      </div>

      {/* Store Name */}
      <p className="mt-2 text-sm font-medium text-pagali-gray-800 text-center truncate w-full">
        {store.NOME}
      </p>

      {/* Category */}
      {store.CATEGORIA_NOME && (
        <span className="mt-0.5 text-[10px] text-pagali-gray-500 bg-pagali-gray-100 px-2 py-0.5 rounded-full truncate max-w-full">
          {store.CATEGORIA_NOME}
        </span>
      )}

      {/* Product Count */}
      {store.PRODUCT_COUNT !== undefined && store.PRODUCT_COUNT > 0 && (
        <span className="mt-1 flex items-center gap-1 text-[10px] text-pagali-gray-400">
          <FiPackage className="text-[10px]" />
          {store.PRODUCT_COUNT} produtos
        </span>
      )}
    </div>
  );
};

const PagaliShopCard: React.FC = () => {
  const navigate = useNavigate();
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadStores = async () => {
      try {
        const data = await ecommerceService.getStores();
        // Filter active stores and sort by featured
        const activeStores = data
          .filter((store) => store.ATIVO !== false)
          .sort((a, b) => {
            // Featured stores first
            if (a.DESTAQUE && !b.DESTAQUE) return -1;
            if (!a.DESTAQUE && b.DESTAQUE) return 1;
            return 0;
          });
        setStores(activeStores);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Erro ao carregar lojas'
        );
      } finally {
        setLoading(false);
      }
    };

    loadStores();
  }, []);

  const handleStoreClick = (storeId: string) => {
    navigate(`/ecommerce/store/${storeId}`);
  };

  return (
    <Card>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-pagali-purple/10 flex items-center justify-center">
            <FiShoppingBag className="text-pagali-purple" />
          </div>
          <h3 className="text-lg font-semibold text-pagali-gray-800">
            Pagali Shop
          </h3>
        </div>
        <button
          onClick={() => navigate('/ecommerce')}
          className="flex items-center gap-1 text-sm text-pagali-primary font-medium hover:underline group"
        >
          Ver todas
          <FiChevronRight className="text-base group-hover:translate-x-0.5 transition-transform" />
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="py-6">
          <LoaderComponent size="sm" />
        </div>
      ) : error ? (
        <p className="text-pagali-error text-sm py-4 text-center">{error}</p>
      ) : stores.length > 0 ? (
        <div className="relative">
          {/* Stores Scroll Container */}
          <div className="flex gap-4 overflow-x-auto pb-3 pt-1 -mx-2 px-2 scrollbar-hide">
            {stores.slice(0, 8).map((store, index) => (
              <StoreItem
                key={store.ID}
                store={store}
                onClick={() => handleStoreClick(store.ID)}
                isFeatured={store.DESTAQUE || index === 0}
              />
            ))}
          </div>

          {/* Gradient fade on right edge */}
          {stores.length > 4 && (
            <div className="absolute right-0 top-0 bottom-3 w-12 bg-gradient-to-l from-white to-transparent pointer-events-none" />
          )}
        </div>
      ) : (
        <div className="py-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-pagali-gray-100 flex items-center justify-center mb-3">
            <FiShoppingBag className="text-3xl text-pagali-gray-300" />
          </div>
          <p className="text-pagali-gray-600 font-medium mb-1">
            Nenhuma loja disponivel
          </p>
          <p className="text-sm text-pagali-gray-400 mb-4">
            As lojas aparecerao aqui quando estiverem disponiveis
          </p>
          <button
            onClick={() => navigate('/ecommerce')}
            className="text-sm text-pagali-primary font-medium hover:underline"
          >
            Explorar Pagali Shop
          </button>
        </div>
      )}
    </Card>
  );
};

export default PagaliShopCard;
